import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { logout } from '../store/slices/authSlice';
import { useSystemSettings, defaultSystemSettings } from '../contexts/SystemSettingsContext';
import { useSocket } from '../hooks/useSocket';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';
import { 
  Calendar,
  BarChart3,
  Search,
  Home,
  LogOut, 
  Menu, 
  X,
  Shield,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Users,
  List,
  Bell,
  CheckCircle2,
  Clipboard,
  Folder,
  Cloud,
  Plus,
  ArrowRight,
  Target,
  TrendingUp,
  HelpCircle,
  UserCircle,
  CheckSquare,
  Inbox,
  LineChart,
  Briefcase,
  MessageSquare,
  Grid3x3,
  LayoutDashboard,
  Store,
  Network,
  User as UserIcon,

  MoreVertical,
  Moon,
  Sun,
  ShoppingCart,
  Maximize,
  Filter,
  Download,
  FileText,
  Circle,
  Book
} from 'lucide-react';

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  read: boolean;
  relatedId: string;
  relatedType: string;
  action?: {
    type: string;
    inviteToken?: string;
    requestId?: string;
  };
  actionStatus?: string;
  createdAt: string;
}

const UserLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [workExpanded, setWorkExpanded] = useState(true);
  const [dashboardsExpanded, setDashboardsExpanded] = useState(true);

  const [pagesExpanded, setPagesExpanded] = useState(false);
  const [userProjects, setUserProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { settings } = useSystemSettings();
  
  const siteName = settings?.siteName || defaultSystemSettings.siteName;
  const API_BASE = API_BASE_URL;
  const token = localStorage.getItem('token');
  const { socket } = useSocket(token);

  // Fetch user projects
  useEffect(() => {
    if (user?._id) {
      fetchUserProjects();
      fetchNotifications();
    }
  }, [user?._id]);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user?._id) return;
    setNotificationsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/notifications`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.data.success) {
        setNotifications(res.data.data.notifications || []);
        setUnreadCount(res.data.data.unreadCount || 0);
      }
    } catch (err: any) {
      console.error('Erreur chargement notifications:', err);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Listen to real-time notifications via socket.io
  useEffect(() => {
    if (!socket) return;

    const handleNotification = (notification: Notification) => {
      console.log('📬 Notification reçue en temps réel:', notification);
      setNotifications(prev => {
        // Éviter les doublons
        const exists = prev.some(n => n._id === notification._id);
        if (exists) return prev;
        return [notification, ...prev];
      });
      setUnreadCount(prev => prev + 1);
    };

    (socket as any).on('notification', handleNotification);

    return () => {
      (socket as any).off('notification', handleNotification);
    };
  }, [socket]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking inside any user-menu element (toggle button or dropdown)
      if (target.closest('[data-user-menu]')) {
        return;
      }
      setUserMenuOpen(null);
    };
    if (userMenuOpen) {
      // Use setTimeout to avoid the opening click from immediately closing the menu
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [userMenuOpen]);

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    try {
      await axios.put(`${API_BASE}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setNotifications(prev => prev.map(n => 
        n._id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Erreur marquer comme lu:', err);
    }
  };

  // Handle notification action (accept)
  const handleNotificationAction = async (notification: Notification) => {
    if (!notification.action) return;
    
    if (notification.action.type === 'accept_invite' && notification.action.inviteToken) {
      navigate(`/teams/accept/${notification.action.inviteToken}`);
    } else if (notification.action.type === 'view_team') {
      navigate(`/projects`);
    } else if (notification.action.type === 'view_task') {
      navigate(`/tasks`);
    }
    
    if (!notification.read) {
      await markAsRead(notification._id);
    }
  };

  // Reject invitation
  const handleRejectInvite = async (notification: Notification, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!notification.action?.inviteToken) return;

    try {
      await axios.post(`${API_BASE}/teams/reject`, {
        token: notification.action.inviteToken
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Mettre à jour la notification localement
      setNotifications(prev => prev.map(n => 
        n._id === notification._id 
          ? { ...n, actionStatus: 'rejected', read: true }
          : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('Erreur refus invitation:', err);
      alert(err.response?.data?.error || 'Erreur lors du refus de l\'invitation');
    }
  };

  // Delete notification
  const handleDeleteNotification = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_BASE}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Retirer la notification de la liste
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      // Décrémenter le compteur si non lue
      const notification = notifications.find(n => n._id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      console.error('Erreur suppression notification:', err);
      alert(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const fetchUserProjects = async () => {
    if (!user?._id) return;
    setProjectsLoading(true);
    try {
      // Ne plus passer userId dans la query - le backend utilise req.user depuis le token
      const res = await axios.get(`${API_BASE}/projects`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // S'assurer que la réponse est toujours un tableau
      const projectsData = res.data?.data;
      if (Array.isArray(projectsData)) {
        setUserProjects(projectsData);
      } else {
        setUserProjects([]);
      }
    } catch (err: any) {
      console.error('Erreur chargement projets:', err);
      setUserProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  };

  // Main pages — management actions live inside their pages, not the sidebar
  const topNavigation = [
    { name: 'Dashboards', href: '/dashboard', icon: Home },
    { name: 'Projects', href: '/projects', icon: Folder },
    { name: 'VaultSphere', href: '/vaultsphere', icon: Cloud },
    { name: 'My Courses', href: '/my-courses', icon: Book },
  ];

  // Indicators section
  const indicatorsNavigation = [
    { name: 'Reports', href: '/reports', icon: LineChart },
    { name: 'Portfolios', href: '/portfolios', icon: Folder },
  ];

  // Team section
  const teamNavigation = [
    { name: 'My workspace', href: '/workspace', icon: Briefcase },
  ];

  // Get project color based on type
  const getProjectColor = (type: string) => {
    switch (type) {
      case 'meeting': return 'text-blue-500';
      case 'workshop': return 'text-green-500';
      case 'deadline': return 'text-orange-500';
      case 'social': return 'text-purple-500';
      case 'project': return 'text-teal-500';
      default: return 'text-gray-400';
    }
  };

  const adminNavigation = user?.role === 'admin' ? [
    { name: 'Admin Panel', href: '/admin', icon: Shield },
  ] : [];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  // Check if we're on projects route
  const isProjectsRoute = location.pathname === '/projects' || location.pathname.startsWith('/projects/');

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const parts = user.name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return user.name.charAt(0).toUpperCase();
  };

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #ecfdf5;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #a7f3d0;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #34d399;
        }
        @keyframes shine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        .shining-text {
          background: linear-gradient(
            90deg,
            #ffffff 0%,
            #10b981 25%,
            #059669 50%,
            #10b981 75%,
            #ffffff 100%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shine 3s linear infinite;
          filter: drop-shadow(0 0 8px rgba(16, 185, 129, 0.5));
        }
        .glow-text {
          text-shadow: 0 0 10px rgba(16, 185, 129, 0.5), 0 0 20px rgba(5, 150, 105, 0.3);
        }
        /* Sidebar 3D - ombre quasi effacée, juste un léger reflux */
        .sidebar-3d {
          box-shadow:
            2px 0 8px 0 rgba(15, 23, 42, 0.04),
            12px 0 42px -10px rgba(15, 23, 42, 0.09),
            26px 0 80px -16px rgba(15, 23, 42, 0.06);
        }
        .sidebar-3d-mobile {
          box-shadow:
            2px 0 8px 0 rgba(15, 23, 42, 0.04),
            12px 0 42px -10px rgba(15, 23, 42, 0.09),
            26px 0 80px -16px rgba(15, 23, 42, 0.06);
        }
        .sidebar-tooltip {
          pointer-events: none;
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translate(8px, -50%);
          white-space: nowrap;
          opacity: 0;
          transition: opacity 150ms ease, transform 150ms ease;
        }
        .sidebar-tooltip-group:hover .sidebar-tooltip {
          opacity: 1;
          transform: translate(12px, -50%);
        }
      `}</style>
      <div className="flex h-screen bg-emerald-50/50">
      {/* Mobile sidebar - vert clair comme la capture */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex flex-col max-w-[280px] w-full h-full sidebar-3d-mobile rounded-r-2xl bg-emerald-50 border border-emerald-200/80 border-l-0">
          <div className="absolute top-3 right-3 z-10">
            <button
              type="button"
              className="flex items-center justify-center h-9 w-9 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0 pt-14 pb-4 overflow-y-auto custom-scrollbar">
            <Link to="/" onClick={() => setSidebarOpen(false)} className="flex-shrink-0 flex items-center gap-3 px-4 py-4 border-b border-emerald-200/80 hover:bg-emerald-100/50 transition-colors block">
              <img src="/logolivinglab.png" alt="Living Lab Logo" className="flex-shrink-0 h-10 w-auto object-contain" />
              <div className="min-w-0">
                <span className="font-bold text-lg leading-tight tracking-tight">
                  <span className="text-[#c1a927]">E-</span><span className="text-[#66b960]">Living</span><span className="text-[#346a44]"> Lab</span>
                </span>
              </div>
            </Link>
            <nav className="px-3 pt-4 space-y-1">
              <div className="px-3 mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Main</span>
              </div>
              {topNavigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors duration-150 ${
                      active
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'text-emerald-900/90 hover:bg-emerald-100/80 hover:text-emerald-900'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`h-5 w-5 flex-shrink-0 ${active ? 'text-emerald-600' : 'text-emerald-700/80 group-hover:text-emerald-800'}`} />
                    <span>{item.name}</span>
                    {active && <div className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                  </Link>
                );
              })}
              <div className="pt-4">
                <div className="flex items-center justify-between px-3 mb-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Projects</span>
                  <button type="button" onClick={() => navigate('/projects')} className="p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 transition-colors">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                {workExpanded && (
                  <div className="space-y-0.5">
                    {projectsLoading ? (
                      <div className="px-3 py-2 text-xs text-emerald-600">Loading…</div>
                    ) : !userProjects?.length ? (
                      <div className="px-3 py-2 text-xs text-emerald-600">No projects</div>
                    ) : (
                      userProjects.map((project) => {
                        const active = location.pathname === `/projects/${project._id}`;
                        const name = (project?.title && project.title.length > 25) ? `${project.title.slice(0, 25)}…` : (project?.title || 'Untitled');
                        return (
                          <Link
                            key={`mobile-project-${project._id}`}
                            to={`/projects/${project._id}`}
                            className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                              active ? 'bg-emerald-100 text-emerald-800' : 'text-emerald-800/90 hover:bg-emerald-100/80 hover:text-emerald-900'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <div className={`h-2 w-2 rounded-full flex-shrink-0 ${getProjectColor(project?.type || 'project')} bg-current`} />
                            <span className="truncate">{name}</span>
                          </Link>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
              <div className="pt-4">
                <span className="block px-3 mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Team</span>
                <div className="space-y-0.5">
                  {teamNavigation.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                          active ? 'bg-emerald-100/90 text-emerald-800' : 'text-slate-600 hover:bg-slate-200/80 hover:text-slate-900'
                        }`}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span>{item.name}</span>
                        <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                      </Link>
                    );
                  })}
                </div>
              </div>
            </nav>
          </div>
          <div className="flex-shrink-0 border-t border-emerald-200/80 p-4 bg-emerald-100/40">
            <div className="relative" data-user-menu>
              <button
                type="button"
                onClick={() => setUserMenuOpen(userMenuOpen === 'mobile-sidebar' ? null : 'mobile-sidebar')}
                className="flex items-center gap-3 w-full rounded-lg p-1 hover:bg-emerald-200/50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-sm ring-1 ring-emerald-200/50 flex-shrink-0">
                  <span className="text-sm font-semibold text-white">{getUserInitials()}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-900 truncate">{user?.name}</p>
                  <p className="text-xs text-emerald-700 truncate capitalize">{user?.email || user?.role || 'User'}</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-emerald-600 transition-transform duration-200 flex-shrink-0 ${userMenuOpen === 'mobile-sidebar' ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen === 'mobile-sidebar' && (
                <div className="absolute bottom-full left-0 right-0 mb-2 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/dashboard'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/profile'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <UserIcon className="h-4 w-4" />
                    Profile
                  </button>
                  {(user?.role === 'admin' || user?.globalRole === 'admin') && (
                    <button
                      onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); navigate('/admin'); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                    >
                      <Shield className="h-4 w-4" />
                      Admin Panel
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(null); setSidebarOpen(false); handleLogout(); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Desktop sidebar - z-index pour que l'ombre passe au-dessus du contenu */}
      <div className="hidden lg:flex lg:flex-shrink-0 relative z-10">
        <aside
          className={`relative flex flex-col bg-emerald-50 rounded-r-2xl sidebar-3d border border-emerald-200/80 border-l-0 transition-[width] duration-300 ease-out ${isSidebarCollapsed ? 'w-[72px]' : 'w-[280px]'}`}
          style={{ perspective: '1000px' }}
        >
          <div className="relative border-b border-emerald-200/80">
            <Link
              to="/"
              className={`flex-shrink-0 flex items-center gap-3 hover:bg-emerald-100/50 transition-colors ${isSidebarCollapsed ? 'px-3 py-4 justify-center' : 'px-4 py-4'}`}
              title="E-Living Lab"
            >
              <img src="/logolivinglab.png" alt="Living Lab Logo" className="flex-shrink-0 h-10 w-auto object-contain" />
              {!isSidebarCollapsed && (
                <div className="min-w-0">
                  <span className="font-bold text-lg leading-tight tracking-tight">
                    <span className="text-[#c1a927]">E-</span><span className="text-[#66b960]">Living</span><span className="text-[#346a44]"> Lab</span>
                  </span>
                </div>
              )}
            </Link>

            {/* Toggle button (innovative, floating) */}
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(v => !v)}
              className="absolute -right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white border border-emerald-200 shadow-md flex items-center justify-center hover:bg-emerald-50 transition-colors"
              aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronRight className={`h-4 w-4 text-emerald-700 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : 'rotate-0'}`} />
            </button>
          </div>

          {/* Search lives in the top header — not duplicated here */}
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 overflow-y-auto custom-scrollbar py-3 px-3">
              <nav className="space-y-0.5">
                {!isSidebarCollapsed && (
                  <div className="px-3 mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Main</span>
                  </div>
                )}
                {topNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`sidebar-tooltip-group relative flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all ${
                        active ? 'bg-emerald-100 text-emerald-800' : 'text-emerald-900/90 hover:bg-emerald-100/80 hover:text-emerald-900'
                      } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-emerald-600' : 'text-emerald-700/80'}`} />
                      {!isSidebarCollapsed && (
                        <>
                          <span>{item.name}</span>
                          {active && <div className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-500" />}
                        </>
                      )}
                      {isSidebarCollapsed && (
                        <span className="sidebar-tooltip rounded-lg bg-white border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-900 shadow-lg">
                          {item.name}
                        </span>
                      )}
                    </Link>
                  );
                })}
                <div className="pt-4 mt-2 border-t border-emerald-200/80">
                  <div className="flex items-center justify-between px-3 mb-2">
                    {!isSidebarCollapsed && (
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Projects</span>
                    )}
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => navigate('/projects')} className="p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-100 transition-colors" aria-label="Voir projets" >
                        <Plus className="h-4 w-4" />
                      </button>
                      {!isSidebarCollapsed && (
                        <button type="button" onClick={() => setWorkExpanded(!workExpanded)} className="p-1 rounded-md text-emerald-600 hover:bg-emerald-100 transition-colors" aria-label={workExpanded ? 'Collapse' : 'Expand'}>
                          {workExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                  {workExpanded && (
                    <div className="space-y-0.5">
                      {                      projectsLoading ? (
                        <div className="px-3 py-2 text-xs text-emerald-600">Loading…</div>
                      ) : !userProjects?.length ? (
                        <div className="px-3 py-2 text-xs text-emerald-600">No projects</div>
                      ) : (
                        userProjects.map((project) => {
                          const active = location.pathname === `/projects/${project._id}`;
                          const name = (project?.title && project.title.length > 20) ? `${project.title.slice(0, 20)}…` : (project?.title || 'Untitled');
                          return (
                            <Link
                              key={project._id}
                              to={`/projects/${project._id}`}
                              className={`sidebar-tooltip-group relative flex items-center gap-3 px-3 py-2 text-sm rounded-xl transition-all ${
                                active ? 'bg-emerald-100 text-emerald-800' : 'text-emerald-800/90 hover:bg-emerald-100/80 hover:text-emerald-900'
                              } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                            >
                              <div className={`h-2 w-2 rounded-full flex-shrink-0 ${getProjectColor(project?.type || 'project')} bg-current`} />
                              {!isSidebarCollapsed && <span className="truncate">{name}</span>}
                              {isSidebarCollapsed && (
                                <span className="sidebar-tooltip rounded-lg bg-white border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-900 shadow-lg">
                                  {name}
                                </span>
                              )}
                            </Link>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </aside>
      </div>

      {/* Main content - z-0 pour que l'ombre de la sidebar soit visible dessus */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden bg-white relative z-0">
        {/* Top nav - fond blanc comme la capture */}
        <header className="relative z-[100] flex-shrink-0 flex h-14 items-center gap-3 border-b border-slate-200 bg-white shadow-sm px-4 lg:px-6">
          <button
            type="button"
            className="p-2 -ml-2 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-emerald-500/30 lg:hidden transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
          {/* Logo only on mobile — the sidebar already shows it on desktop */}
          <Link to="/" className="lg:hidden flex items-center gap-2 flex-shrink-0">
            <img src="/logolivinglab.png" alt="Living Lab Logo" className="h-8 w-auto object-contain" />
            <span className="hidden sm:inline font-bold text-base leading-tight tracking-tight">
              <span className="text-[#c1a927]">E-</span><span className="text-[#66b960]">Living</span><span className="text-[#346a44]"> Lab</span>
            </span>
          </Link>
          <div className="flex-1 flex justify-center min-w-0">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search…"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate('/search');
                }}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-slate-200 rounded-full text-slate-900 placeholder-slate-400 bg-slate-50 hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-400 transition-all"
              />
            </div>
          </div>
          <div className="hidden lg:flex items-center gap-2 px-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  if (!notificationsOpen) fetchNotifications();
                }}
                className="relative p-2.5 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <>
                  <div className="fixed inset-0 z-[9998]" onClick={() => setNotificationsOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-[9999] max-h-96 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                      <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="text-xs font-medium text-emerald-600">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-80 custom-scrollbar">
                      {notificationsLoading ? (
                        <div className="p-4 text-center text-slate-500 text-sm">Loading…</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">No notifications</div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {notifications.map((notification) => (
                            <div
                              key={notification._id}
                              className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                                !notification.read ? 'bg-emerald-50/70' : ''
                              }`}
                              onClick={() => {
                                handleNotificationAction(notification);
                                setNotificationsOpen(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`flex-shrink-0 mt-0.5 ${
                                  notification.type === 'team_invite' ? 'text-blue-500' :
                                  notification.type === 'team_join_request' ? 'text-emerald-500' :
                                  notification.type === 'task_assigned' ? 'text-purple-500' :
                                  'text-slate-400'
                                }`}>
                                  {notification.type === 'team_invite' && <Users className="h-5 w-5" />}
                                  {notification.type === 'team_join_request' && <UserIcon className="h-5 w-5" />}
                                  {notification.type === 'task_assigned' && <CheckSquare className="h-5 w-5" />}
                                  {!['team_invite', 'team_join_request', 'task_assigned'].includes(notification.type) && <Bell className="h-5 w-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                                        {notification.title}
                                      </p>
                                      <p className="text-xs text-slate-500 mt-1">{notification.message}</p>
                                      <p className="text-xs text-slate-400 mt-1">
                                        {new Date(notification.createdAt).toLocaleDateString('fr-FR', {
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => handleDeleteNotification(notification._id, e)}
                                      className="flex-shrink-0 p-1 text-slate-400 hover:text-red-600 transition-colors"
                                      title="Supprimer"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                  {notification.action && notification.action.type === 'accept_invite' && !notification.actionStatus && (
                                    <div className="mt-3 flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNotificationAction(notification);
                                          setNotificationsOpen(false);
                                        }}
                                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                                      >
                                        Accepter
                                      </button>
                                      <button
                                        onClick={(e) => handleRejectInvite(notification, e)}
                                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                      >
                                        Refuser
                                      </button>
                                    </div>
                                  )}
                                  {notification.actionStatus === 'rejected' && (
                                    <div className="mt-2">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                                        Declined
                                      </span>
                                    </div>
                                  )}
                                  {notification.actionStatus === 'accepted' && (
                                    <div className="mt-2">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-800 rounded">
                                        Accepted
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {!notification.read && (
                                  <div className="flex-shrink-0">
                                    <div className="h-2 w-2 bg-emerald-500 rounded-full" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div data-user-menu className="relative ml-2 pl-4 border-l border-slate-200">
              <button
                type="button"
                onClick={() => setUserMenuOpen(userMenuOpen === 'desktop-header' ? null : 'desktop-header')}
                className="flex items-center gap-3 rounded-xl px-2 py-1.5 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
              >
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-sm ring-1 ring-emerald-200/50">
                  <span className="text-sm font-semibold text-white">{getUserInitials()}</span>
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium text-slate-900">{user?.name || 'User'}</span>
                  <span className="text-xs text-slate-500">{user?.role || 'Membre'}</span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${userMenuOpen === 'desktop-header' ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen === 'desktop-header' && (
                <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50 animate-in fade-in-0 slide-in-from-top-2 duration-150">
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <UserIcon className="h-4 w-4" />
                    Profile
                  </button>
                  {(user?.role === 'admin' || user?.globalRole === 'admin') && (
                    <button
                      onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                      className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                    >
                      <Shield className="h-4 w-4" />
                      Admin Panel
                    </button>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex lg:hidden items-center gap-2 px-4">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  if (!notificationsOpen) fetchNotifications();
                }}
                className="relative p-2.5 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 bg-emerald-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <>
                  <div className="fixed inset-0 z-[9998] bg-black/20" onClick={() => setNotificationsOpen(false)} />
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-200 z-[9999] max-h-96 overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                      <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <span className="text-xs font-medium text-emerald-600">{unreadCount} non lue{unreadCount > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-80 custom-scrollbar">
                      {notificationsLoading ? (
                        <div className="p-4 text-center text-slate-500 text-sm">Loading…</div>
                      ) : notifications.length === 0 ? (
                        <div className="p-4 text-center text-slate-500 text-sm">No notifications</div>
                      ) : (
                        <div className="divide-y divide-slate-100">
                          {notifications.map((notification) => (
                            <div
                              key={notification._id}
                              className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                                !notification.read ? 'bg-emerald-50/70' : ''
                              }`}
                              onClick={() => {
                                handleNotificationAction(notification);
                                setNotificationsOpen(false);
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`flex-shrink-0 mt-0.5 ${
                                  notification.type === 'team_invite' ? 'text-blue-500' :
                                  notification.type === 'team_join_request' ? 'text-emerald-500' :
                                  notification.type === 'task_assigned' ? 'text-purple-500' :
                                  'text-slate-400'
                                }`}>
                                  {notification.type === 'team_invite' && <Users className="h-5 w-5" />}
                                  {notification.type === 'team_join_request' && <UserIcon className="h-5 w-5" />}
                                  {notification.type === 'task_assigned' && <CheckSquare className="h-5 w-5" />}
                                  {!['team_invite', 'team_join_request', 'task_assigned'].includes(notification.type) && <Bell className="h-5 w-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                                        {notification.title}
                                      </p>
                                      <p className="text-xs text-slate-500 mt-1">{notification.message}</p>
                                      <p className="text-xs text-slate-400 mt-1">
                                        {new Date(notification.createdAt).toLocaleDateString('fr-FR', {
                                          day: 'numeric',
                                          month: 'short',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </p>
                                    </div>
                                    <button
                                      onClick={(e) => handleDeleteNotification(notification._id, e)}
                                      className="flex-shrink-0 p-1 text-slate-400 hover:text-red-600 transition-colors"
                                      title="Supprimer"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </div>
                                  {notification.action && notification.action.type === 'accept_invite' && !notification.actionStatus && (
                                    <div className="mt-3 flex items-center gap-2">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNotificationAction(notification);
                                          setNotificationsOpen(false);
                                        }}
                                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                                      >
                                        Accepter
                                      </button>
                                      <button
                                        onClick={(e) => handleRejectInvite(notification, e)}
                                        className="flex-1 px-3 py-1.5 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                      >
                                        Refuser
                                      </button>
                                    </div>
                                  )}
                                  {notification.actionStatus === 'rejected' && (
                                    <div className="mt-2">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                                        Declined
                                      </span>
                                    </div>
                                  )}
                                  {notification.actionStatus === 'accepted' && (
                                    <div className="mt-2">
                                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-800 rounded">
                                        Accepted
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {!notification.read && (
                                  <div className="flex-shrink-0">
                                    <div className="h-2 w-2 bg-emerald-500 rounded-full" />
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              data-user-menu
              onClick={() => setUserMenuOpen(userMenuOpen === 'mobile-header' ? null : 'mobile-header')}
              className="relative h-8 w-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center ring-1 ring-emerald-200/50 shadow-sm hover:ring-2 hover:ring-emerald-400/50 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
            >
              <span className="text-xs font-semibold text-white">
                {getUserInitials()}
              </span>
            </button>
            {userMenuOpen === 'mobile-header' && (
              <div className="absolute right-4 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-100 py-1 z-50">
                <div className="px-3 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/dashboard'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </button>
                <button
                  onClick={() => { setUserMenuOpen(null); navigate('/profile'); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                >
                  <UserIcon className="h-4 w-4" />
                  Profile
                </button>
                {(user?.role === 'admin' || user?.globalRole === 'admin') && (
                  <button
                    onClick={() => { setUserMenuOpen(null); navigate('/admin'); }}
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-left"
                  >
                    <Shield className="h-4 w-4" />
                    Admin Panel
                  </button>
                )}
                <div className="border-t border-gray-100 my-1" />
                <button
                  onClick={() => { setUserMenuOpen(null); handleLogout(); }}
                  className="flex items-center gap-3 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-white custom-scrollbar flex flex-col min-h-0">
          {location.pathname === '/vaultsphere' ? (
            <Outlet />
          ) : (
            <div className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                <Outlet />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
    </>
  );
};

export default UserLayout;

